import { useState, useEffect, useRef } from "react";
import { DayPicker } from "react-day-picker";
import { Calendar as CalendarIcon, X } from "lucide-react";
import {
  it as itLocale, fr as frLocale, es as esLocale, de as deLocale,
  pt as ptLocale, nl as nlLocale, pl as plLocale, sv as svLocale,
  nb as nbLocale, fi as fiLocale, da as daLocale, cs as csLocale, el as elLocale,
} from "date-fns/locale";
import { format, parseISO, isValid as isValidDate } from "date-fns";

const LOCALES = {
  IT: itLocale, FR: frLocale, ES: esLocale, DE: deLocale, PT: ptLocale,
  BE: frLocale, NL: nlLocale, PL: plLocale, SE: svLocale, NO: nbLocale,
  FI: fiLocale, DK: daLocale, CZ: csLocale, GR: elLocale, CH: deLocale,
  GBZ: undefined, IRL: undefined, UK: undefined,
};

const LABEL_FORMATS = {
  IT: "dd/MM/yyyy", FR: "dd/MM/yyyy", ES: "dd/MM/yyyy", DE: "dd.MM.yyyy",
  PT: "dd/MM/yyyy", BE: "dd/MM/yyyy", NL: "dd-MM-yyyy", PL: "dd.MM.yyyy",
  SE: "yyyy-MM-dd", NO: "dd.MM.yyyy", FI: "dd.MM.yyyy", DK: "dd-MM-yyyy",
  CZ: "dd.MM.yyyy", GR: "dd/MM/yyyy", CH: "dd.MM.yyyy",
};

const PLACEHOLDERS = {
  IT: "Seleziona data", FR: "Selectionner date", ES: "Seleccionar fecha",
  DE: "Datum wahlen", PT: "Selecionar data", BE: "Selectionner date",
  NL: "Datum kiezen", PL: "Wybierz date", SE: "Valj datum", NO: "Velg dato",
  FI: "Valitse paivamaara", DK: "Vaelg dato", CZ: "Vybrat datum",
  GR: "Epilogi imerominias", CH: "Datum wahlen",
};

const TODAY_LABEL = {
  IT: "Oggi", FR: "Aujourd'hui", ES: "Hoy", DE: "Heute", PT: "Hoje",
  NL: "Vandaag", PL: "Dzis", SE: "Idag", NO: "I dag", FI: "Tanaan",
  DK: "I dag", CZ: "Dnes", GR: "Simera", CH: "Heute",
};

const CLEAR_LABEL = {
  IT: "Cancella", FR: "Effacer", ES: "Borrar", DE: "Loschen", PT: "Limpar",
  NL: "Wissen", PL: "Wyczysc", SE: "Rensa", NO: "Slett", FI: "Tyhjenna",
  DK: "Ryd", CZ: "Smazat", GR: "Diagrafi", CH: "Loschen",
};

/**
 * LoJack-themed date picker — popover with react-day-picker, dark theme,
 * localized formatting and Today/Clear shortcuts. Falls back to plain
 * <input type="date"> on devices where popovers are awkward (mobile is
 * supported but the native picker can be triggered by clicking the input).
 *
 * Props:
 *   value: ISO string YYYY-MM-DD (or empty)
 *   onChange: (isoString) => void
 *   locale: country code (IT/FR/ES/DE/...) — drives format and language
 *   maxDate / minDate: Date | undefined
 *   testId: string
 */
export const DatePickerField = ({
  value,
  onChange,
  locale = "IT",
  maxDate,
  minDate,
  testId,
  className = "",
  required = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const code = (locale || "IT").toUpperCase();
  const fnsLocale = LOCALES[code];
  const fmt = LABEL_FORMATS[code] || "dd/MM/yyyy";
  const placeholder = PLACEHOLDERS[code] || PLACEHOLDERS.IT;

  const selectedDate = value ? parseISO(value) : undefined;
  const safeSelected = selectedDate && isValidDate(selectedDate) ? selectedDate : undefined;

  const displayValue = safeSelected ? format(safeSelected, fmt, { locale: fnsLocale }) : "";

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSelect = (date) => {
    if (!date) {
      onChange("");
      return;
    }
    onChange(format(date, "yyyy-MM-dd"));
    setOpen(false);
  };

  const handleToday = () => {
    const t = new Date();
    onChange(format(t, "yyyy-MM-dd"));
    setOpen(false);
  };

  const handleClear = () => {
    onChange("");
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`} data-testid={testId ? `${testId}-wrapper` : undefined}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-left text-white focus:border-lojack-red focus:outline-none flex items-center justify-between gap-2 hover:border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={testId}
        aria-label={placeholder}
      >
        <span className={displayValue ? "text-white" : "text-gray-500"}>
          {displayValue || placeholder}
        </span>
        <CalendarIcon size={18} className="text-lojack-red flex-shrink-0" />
      </button>

      {/* Hidden native input so the parent <form required> still applies */}
      {required && (
        <input
          type="text"
          value={value || ""}
          onChange={() => {}}
          required
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      )}

      {open && (
        <div
          className="absolute z-50 mt-2 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl p-3 min-w-[280px]"
          style={{ left: 0 }}
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <DayPicker
            mode="single"
            selected={safeSelected}
            onSelect={handleSelect}
            locale={fnsLocale}
            defaultMonth={safeSelected || new Date(1990, 0, 1)}
            captionLayout="dropdown-buttons"
            fromYear={1900}
            toYear={new Date().getFullYear()}
            disabled={[
              ...(maxDate ? [{ after: maxDate }] : []),
              ...(minDate ? [{ before: minDate }] : []),
            ]}
            classNames={{
              months: "flex flex-col",
              month: "space-y-2",
              caption: "flex justify-center pt-1 relative items-center px-2",
              caption_label: "hidden",
              caption_dropdowns: "flex gap-2",
              dropdown: "bg-[#0A0A0A] border border-gray-700 rounded-md text-white text-sm px-2 py-1 focus:border-lojack-red focus:outline-none",
              nav: "space-x-1 flex items-center",
              nav_button: "h-7 w-7 bg-transparent text-gray-400 hover:text-white hover:bg-[#2A2A2A] rounded inline-flex items-center justify-center transition-colors",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse",
              head_row: "flex",
              head_cell: "text-gray-500 rounded-md w-9 font-normal text-[0.75rem] uppercase",
              row: "flex w-full mt-1",
              cell: "relative p-0 text-center text-sm",
              day: "h-9 w-9 p-0 font-normal text-gray-300 hover:bg-lojack-red/20 hover:text-white rounded-md transition-colors inline-flex items-center justify-center cursor-pointer",
              day_selected: "bg-lojack-red text-white hover:bg-lojack-red hover:text-white font-semibold",
              day_today: "border border-lojack-red/60 text-white",
              day_outside: "text-gray-700",
              day_disabled: "text-gray-700 opacity-40 cursor-not-allowed hover:bg-transparent hover:text-gray-700",
              day_hidden: "invisible",
            }}
          />

          {/* Footer with Today / Clear */}
          <div className="flex items-center justify-between border-t border-gray-800 mt-2 pt-2 px-1">
            <button
              type="button"
              onClick={handleToday}
              className="text-lojack-red text-xs hover:underline"
              data-testid={testId ? `${testId}-today` : undefined}
            >
              {TODAY_LABEL[code] || TODAY_LABEL.IT}
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 text-xs hover:text-white flex items-center gap-1"
              data-testid={testId ? `${testId}-clear` : undefined}
            >
              <X size={12} /> {CLEAR_LABEL[code] || CLEAR_LABEL.IT}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
